/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-use-before-define */
import {
  isJsonApiErrorDocument,
  JsonApiError,
  JsonApiValidationError,
} from "@gms/models/src/api/jsonApiErrorResponse";
import {
  Json,
  JsonApiData,
  JsonApiRelationshipData,
  ParsedJsonApiItem,
} from "@gms/models/src/api/types";
import Response from "@gms/network/src/response";
import { useWebWorkerFn } from "@vueuse/core";
import { AxiosError } from "axios";

/**
 * Helper class that defines a 'visited' type. Used to prevent endless recursion of relationships.
 */
interface Visited {
  type: string;

  id: string;
}

type IncludeEntry = JsonApiData<string, Record<string, Json>, string>;
type IncludeCollection = ReadonlyArray<IncludeEntry> | undefined;
type IncludeIndex = Map<string, IncludeEntry>;

type JsonApiParsedItem = ParsedJsonApiItem<string> & Record<string, unknown>;
type JsonApiParsedRelationship =
  | JsonApiParsedItem
  | Array<JsonApiParsedItem | undefined>
  | undefined;

type JsonApiDocumentData =
  | IncludeEntry
  | Array<IncludeEntry>
  | JsonApiRelationshipData
  | Array<JsonApiRelationshipData>
  | null;

interface JsonApiDocumentBody {
  data?: JsonApiDocumentData;
  included?: ReadonlyArray<IncludeEntry>;
  [key: string]: unknown;
}

type JsonApiErrorObject = {
  [key: string]: unknown;
  status?: string | number;
};

interface JsonApiErrorDocument {
  errors?: Array<JsonApiErrorObject>;
}

type JsonApiErrorInput = {
  status: string;
  title: string;
  detail?: string;
  code?: string;
  [key: string]: unknown;
};

function buildIncludeIndex(
  includes: IncludeCollection,
): IncludeIndex | undefined {
  if (!includes || includes.length === 0) {
    return undefined;
  }
  const index: IncludeIndex = new Map();
  includes.forEach((entry) => {
    const id = `${entry.id}`;
    const rawKey = `${entry.type}:${id}`;
    if (!index.has(rawKey)) {
      index.set(rawKey, entry);
    }
  });
  return index;
}

function getIncludedResource(
  includeIndex: IncludeIndex,
  type: string,
  id: string,
): IncludeEntry | undefined {
  const normalizedId = `${id}`;
  const direct = includeIndex.get(`${type}:${normalizedId}`);
  if (direct) {
    return direct;
  }
  return undefined;
}

/**
 * Reads the relationships one by one. Checks the includes for matching entries and builds
 * a complete relationship object out of it.
 * @param objectData The data for this object
 * @param includes The full includes array
 * @param visited A list of strings that contain the singular types that were already visited in
 * this execution path, to prevent endless recursion
 * @return object The object that represents the relationship for the given objectData
 */
function parseRelationships(
  objectData: JsonApiData<string, Record<string, Json>, string>,
  includes: IncludeCollection,
  includeIndex: IncludeIndex | undefined,
  visited: Array<Visited>,
): Record<string, JsonApiParsedRelationship> {
  const result: Record<string, JsonApiParsedRelationship> = {};

  // Process each relationship of objectData individually
  Object.entries(objectData.relationships ?? []).forEach(([key, value]) => {
    // If the data attribute inside this relationship is non-existent, we set
    // null for this relationship
    if (
      value.data === undefined ||
      value.data === null ||
      (Array.isArray(value.data) && value.data.length === 0)
    ) {
      result[key] = undefined;
      return;
    }

    // Now we know that the relationship contains data
    const processedData = processData(
      value.data,
      includes,
      includeIndex,
      visited,
    );

    // This is a safety measure, just in case the if-statement above didn't catch everything.
    if (!processedData) {
      return;
    }

    // If the includes are not defined, we don't need to proceed
    if (includes === undefined || includeIndex === undefined) {
      result[key] = processedData;
      return;
    }

    // Helper method to get the include for one item and merge it with the existing object
    const processSingleItem = (
      item: JsonApiParsedItem,
    ): JsonApiParsedItem | undefined => {
      const matchingInclude = getIncludedResource(
        includeIndex,
        item.apiType,
        item.apiId,
      );
      if (matchingInclude === undefined) {
        return undefined;
      }
      const d = processData(matchingInclude, includes, includeIndex, visited);
      return { ...item, ...d };
    };

    // Differentiate between the cases where there is only one or multiple relationships
    if (Array.isArray(processedData)) {
      result[key] = processedData.map((relationship) =>
        processSingleItem(relationship),
      );
    } else if (typeof processedData === "object" && processedData !== null) {
      result[key] = processSingleItem(processedData);
    } else {
      throw new Error("The relationship object had an unexpected type.");
    }
  });

  return result;
}

/**
 * Checks if the given 'visited' array already contains the type and id
 * @param visited The array of 'visited' objects
 * @param type
 * @param id
 */
function visitedIncludesTypeAndId(
  visited: Array<Visited>,
  type: string,
  id: string,
) {
  const normalizedId = `${id}`;
  const visitedMatches = visited.filter(
    (v) => v.type === type && v.id === normalizedId,
  );

  return visitedMatches.length > 0;
}

function isJsonApiResource(
  value:
    | JsonApiData<string, Record<string, Json>, string>
    | JsonApiRelationshipData,
): value is JsonApiData<string, Record<string, Json>, string> {
  return "attributes" in value || "relationships" in value;
}

/**
 * @param objectData An object of form { type, id, attributes, ?relationships, ?links }
 * @param includes The entire includes object
 * @param visited A list of strings containing the singular types that were already parsed in this
 * execution path, to prevent endless recursion
 * @return object The entire object, containing the attributes,
 * as well as relationships with included data
 */
function parseObject(
  objectData:
    | JsonApiData<string, Record<string, Json>, string>
    | JsonApiRelationshipData,
  includes: IncludeCollection,
  includeIndex: IncludeIndex | undefined,
  visited: Array<Visited>,
): JsonApiParsedItem {
  // Initialize the base object
  const base = {
    apiId: objectData.id,
    apiLinks: objectData.links,
    apiType: objectData.type,
  } as JsonApiParsedItem;

  if (!isJsonApiResource(objectData)) {
    return base;
  }

  const otherKeys = Object.keys(objectData).filter(
    (key) => !["type", "id"].includes(key),
  );

  let addedToVisited = false;
  // In this case, the objectData is not a relationship object, so add it to visited
  if (otherKeys.length >= 1) {
    if (
      visitedIncludesTypeAndId(visited, objectData.type, `${objectData.id}`)
    ) {
      return base;
    }
    visited.push({
      type: objectData.type,
      id: `${objectData.id}`,
    });
    addedToVisited = true;
  }

  let result: JsonApiParsedItem = {
    ...base,
    ...objectData.attributes,
  };

  if ("relationships" in objectData) {
    const relationships = parseRelationships(
      objectData,
      includes,
      includeIndex,
      visited,
    );
    result = { ...result, ...relationships };
  }

  if (addedToVisited) {
    visited.pop();
  }

  return result;
}

/**
 * Processes the given data. Differentiates between data arrays and objects
 * @param data The entire data object, as returned by an API response
 * @param includes The unmodified included array
 * @param visited A list of strings containing the singular types that were already parsed in this
 * execution path, to prevent endless recursion
 */
function processData<
  T extends
    | JsonApiData<string, Record<string, Json>, string>
    | JsonApiData<string, Record<string, Json>, string>[]
    | JsonApiRelationshipData
    | Array<JsonApiRelationshipData>
    | null
    | undefined,
>(
  data: T,
  includes: IncludeCollection,
  includeIndex: IncludeIndex | undefined,
  visited: Array<Visited>,
): Array<JsonApiParsedItem> | JsonApiParsedItem | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  if (Array.isArray(data)) {
    const results: Array<JsonApiParsedItem> = [];
    data.forEach((el) => {
      results.push(parseObject(el, includes, includeIndex, visited));
    });
    return results;
  }

  if (typeof data === "object") {
    return parseObject(data, includes, includeIndex, visited);
  }

  throw Error("The data of the response had an unexpected type.");
}

/**
 * Parses the JSON API response from its original form to a more shallow
 * form without the boilerplate data
 *
 * For example, if you get a response like this:
 * ```json
 * {
 *     "data": {
 *         "type": "reservations",
 *         "id": "1",
 *         "attributes": { "start": "2023-10-13T13:05:00Z" },
 *         "relationships": {
 *             "device": {
 *                 "data": {
 *                     "type": "devices",
 *                     "id": "2"
 *                 }
 *             }
 *         },
 *         "links": {
 *             "self": "some/link"
 *         }
 *     },
 *     "included": [
 *         {
 *             "type": "devices",
 *             "id": "2",
 *             "attributes": { "name": "Device 1" }
 *         }
 *     ]
 * }
 * ```
 *
 * The parsed object would contain the attributes of the reservation as
 * top level attributes, as well as the devices associated with it as
 * another attribute like this:
 *
 * ```json
 * {
 *     "apiId": "1",
 *     "apiLinks": {
 *         "self": "some/link"
 *     },
 *     "apiType": "reservations",
 *     "start": "2023-10-13T13:05:00Z",
 *     "device": {
 *         "apiId": "2",
 *         "apiType": "devices",
 *         "name": "Device 1"
 *     }
 * }
 * ```
 *
 * @param response The original response as returned by the server
 * @return object An object that contains all the data of the original response,
 * but in a more shallow format
 */
export default function parseJsonApiResponse(
  response: Response<unknown>,
): Promise<Array<JsonApiParsedItem>> {
  if (response.status > 202) {
    throw Error(
      `A json:api response with status ${response.status} can't be parsed.`,
    );
  }

  const body = response.data as JsonApiDocumentBody;
  return parseJsonApiData(body);
}

/**
 * Starts the processing chain using the given json object. Expects the given jsonBody to be a valid
 * {json:api} response from the backend, or at least in the same format.
 * @param jsonBody The data that should be parsed
 */
export function parseJsonApiData(
  jsonBody: JsonApiDocumentBody,
): Promise<Array<JsonApiParsedItem>> {
  const { workerFn } = useWebWorkerFn(
    (body: JsonApiDocumentBody) => {
      const { data, included } = body;

      // This will start the processing chain.
      // For each data object this will process the attributes.
      // After that it will process all relationships recursively until none are left.
      // It also checks for potential endless recursions such as author -> article -> author
      // In that case the deeper author object won't contain the article
      // anymore to prevent further recursion
      const includeIndex = buildIncludeIndex(included);
      const res = processData(data, included, includeIndex, []);
      if (!Array.isArray(res)) {
        return res ? [res] : [];
      }
      return res;
    },
    {
      localDependencies: [
        processData,
        parseObject,
        parseRelationships,
        visitedIncludesTypeAndId,
        isJsonApiResource,
        buildIncludeIndex,
        getIncludedResource,
      ],
    },
  );
  return workerFn(jsonBody);
}

/**
 * Returns
 * @param error
 */
export function parseJsonApiError(error: Error) {
  if (!(error instanceof AxiosError)) {
    return error;
  }

  const axiosError = error as AxiosError<JsonApiErrorDocument>;
  const response = axiosError.response;
  if (!response) {
    return error;
  }

  const contentType = response.headers?.["content-type"];
  if (contentType !== "application/vnd.api+json") {
    return error;
  }

  const body = response.data as JsonApiErrorDocument | undefined;
  if (!isJsonApiErrorDocument(body)) {
    return error;
  }

  if (response.status === 422) {
    return new JsonApiValidationError<any>(body, { cause: error });
  }

  return new JsonApiError(body, { cause: error });
}
